// ===== Depth of Market: floating window with live Binance order book (crypto) or simulated book =====
import { useEffect, useRef, useState } from "react";
import { useStore, setUI, CRYPTO, openPosition, round } from "./store";
import { openDepth } from "./binance";
import { openMenu } from "./ContextMenu";
import { I } from "./Icons";

const LEVELS = 15;

// Synthetic book around current bid/ask for non-live symbols
const simBook = (s) => {
  const step = s.point * Math.max(1, Math.round(s.spread / 4));
  const lot = () => +(Math.random() * 20 + 0.5).toFixed(2);
  return {
    asks: Array.from({ length: LEVELS }, (_, i) => [round(s.ask + i * step, s.digits), lot()]),
    bids: Array.from({ length: LEVELS }, (_, i) => [round(s.bid - i * step, s.digits), lot()]),
  };
};

const fmtVol = (v) => (v >= 1000 ? v.toFixed(0) : v >= 1 ? v.toFixed(2) : v.toFixed(4));

const DomWin = ({ symbol }) => {
  const st = useStore();
  const s = st.symbols[symbol];
  const pair = CRYPTO[symbol];
  const live = st.liveFeed && !!pair;
  const [book, setBook] = useState(null);
  const [status, setStatus] = useState("simulated");
  const [lot, setLot] = useState("0.10");
  const [pos, setPos] = useState({ x: Math.max(20, window.innerWidth - 620), y: 110 });
  const drag = useRef(null);
  const scrollRef = useRef(null);
  const spreadRef = useRef(null);

  useEffect(() => {
    setBook(null);
    if (!live) { setStatus("simulated"); return undefined; }
    return openDepth(pair, setBook, setStatus);
  }, [pair, live]);

  const loading = live && !book;
  const data = loading ? { asks: [], bids: [] } : live ? book : simBook(s);

  // keep the spread row centred when the symbol changes or the book first loads
  useEffect(() => {
    const sc = scrollRef.current, sp = spreadRef.current;
    if (sc && sp) sc.scrollTop = sp.offsetTop - sc.clientHeight / 2;
  }, [symbol, loading]);
  const asks = data.asks.slice(0, LEVELS);
  const bids = data.bids.slice(0, LEVELS);
  const maxV = Math.max(...asks.map((a) => a[1]), ...bids.map((b) => b[1]), 1e-9);
  const sumA = asks.reduce((a, x) => a + x[1], 0), sumB = bids.reduce((a, x) => a + x[1], 0);
  const bestAsk = asks[0] ? asks[0][0] : s.ask, bestBid = bids[0] ? bids[0][0] : s.bid;
  const dg = s.digits;

  const down = (e) => {
    drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    const mv = (ev) => setPos({ x: ev.clientX - drag.current.x, y: Math.max(0, ev.clientY - drag.current.y) });
    const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup", up);
  };
  const levelMenu = (e, price, side) => openMenu(e, [
    { label: `${side === "ask" ? "Sell Limit" : "Buy Limit"} ${lot} at ${price.toFixed(dg)}`, onClick: () => setUI({ dialog: { kind: "order", symbol, mode: "pending", pendingType: side === "ask" ? "sell_limit" : "buy_limit", price, volume: lot } }) },
    { label: `${side === "ask" ? "Buy Stop" : "Sell Stop"} ${lot} at ${price.toFixed(dg)}`, onClick: () => setUI({ dialog: { kind: "order", symbol, mode: "pending", pendingType: side === "ask" ? "buy_stop" : "sell_stop", price, volume: lot } }) },
    { sep: true },
    { label: "Create Alert at this price", onClick: () => setUI({ dialog: { kind: "alert", symbol, value: price, cond: price > s.bid ? "Bid >" : "Bid <" } }) },
  ]);
  const row = (side, [p, v], i) => (
    <tr key={side + i} className={`dom-${side}`} data-testid={`dom-${side}-row-${i}`} onContextMenu={(e) => levelMenu(e, p, side)}>
      <td className="dom-vol l">{side === "bid" && <><div className="dom-bar bid" style={{ width: `${(v / maxV) * 100}%` }} /><span>{fmtVol(v)}</span></>}</td>
      <td className="dom-price">{p.toFixed(dg)}</td>
      <td className="dom-vol l">{side === "ask" && <><div className="dom-bar ask" style={{ width: `${(v / maxV) * 100}%` }} /><span>{fmtVol(v)}</span></>}</td>
    </tr>
  );

  return (
    <div className="dom-win" style={{ left: pos.x, top: pos.y }} data-testid="dom-window">
      <div className="dlg-title" onMouseDown={down}>
        <span>Depth Of Market: {symbol}</span>
        <button className="dlg-x" data-testid="dom-close" onClick={() => setUI({ dom: null })}><I.close /></button>
      </div>
      <div className="dom-top">
        <select data-testid="dom-symbol" value={symbol} onChange={(e) => setUI({ dom: e.target.value })}>
          {st.watch.map((n) => <option key={n}>{n}</option>)}
        </select>
        <span className="dom-src" data-testid="dom-status"><span className={`feed-dot ${live ? status : "off"}`} />&nbsp;{live ? `Binance ${status}` : "simulated book"}</span>
      </div>
      <div className="dom-trade">
        <button className="btn-sell" data-testid="dom-sell" onClick={() => openPosition(symbol, "sell", +lot || 0.01, 0, 0, "DOM")}>Sell</button>
        <input data-testid="dom-lot" value={lot} onChange={(e) => setLot(e.target.value)} />
        <button className="btn-buy" data-testid="dom-buy" onClick={() => openPosition(symbol, "buy", +lot || 0.01, 0, 0, "DOM")}>Buy</button>
      </div>
      <div className="dom-scroll" ref={scrollRef}>
        <table className="mt-table dom-table" data-testid="dom-table">
          <thead><tr><th className="l">Bid volume</th><th>Price</th><th className="l">Ask volume</th></tr></thead>
          <tbody>
            {[...asks].reverse().map((a, i) => row("ask", a, asks.length - 1 - i))}
            <tr className="dom-spread" data-testid="dom-spread" ref={spreadRef}><td colSpan={3}>{loading ? "Loading order book from Binance..." : <>Spread: {Math.round((bestAsk - bestBid) / s.point)} pts &nbsp;·&nbsp; Mid {((bestAsk + bestBid) / 2).toFixed(dg)}</>}</td></tr>
            {bids.map((b, i) => row("bid", b, i))}
          </tbody>
        </table>
      </div>
      <div className="dom-foot" data-testid="dom-imbalance">
        <span className="pos-b">Bids {fmtVol(sumB)}</span>
        <div className="dom-imb"><div style={{ width: `${(sumB / (sumA + sumB || 1)) * 100}%` }} /></div>
        <span className="neg-a">Asks {fmtVol(sumA)}</span>
      </div>
    </div>
  );
};

export default function DepthOfMarket() {
  const { dom } = useStore();
  return dom ? <DomWin symbol={dom} /> : null;
}
